import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Dropdown, Tooltip, useModal } from '@ohif/ui';
import { useAppConfig } from '@state';
import HttpClient from '@utils/HttpClient';

import AdvancedFilters from './AdvancedFilters';
import Countdown from '../Countdown';
import Icon from '../Icon';
import InputGroup from '../InputGroup';
import SvgLoading from '@components/SvgLoading';

import './StudyListFilter.css';

export interface FilterMetaOption {
  value?: string;
  label?: string;
}

export interface FilterMetaItem {
  name: string;
  displayName: string;
  inputType: 'Text' | 'MultiSelect' | 'DateRange' | 'None';
  isSortable: boolean;
  gridCol: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
  option?: FilterMetaOption[];
}

export interface StudyListFilterProps {
  filtersMeta: FilterMetaItem[];
  filterValues: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  clearFilters: () => void;
  isFiltering: boolean;
  numOfStudies: number;
  onUploadClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  getDataSourceConfigurationComponent?: () => React.ReactNode;
  refresh?: () => void;
  isLoadingData?: boolean;
  setIsLoading?: (isLoading: boolean) => void;
}

const IS_SORTING_ENABLED = true;

const StudyListFilter: React.FC<StudyListFilterProps> = ({
  filtersMeta = [],
  filterValues = {},
  onChange,
  clearFilters,
  isFiltering = false,
  numOfStudies = 0,
  onUploadClick,
  getDataSourceConfigurationComponent,
  refresh,
  isLoadingData = false,
  setIsLoading,
}) => {
  const { t } = useTranslation();
  const { show } = useModal();
  const { filteredStudies, setFilteredStudies, filterApplied, setFilterApplied } = useAppConfig();

  const [customFilters, setCustomFilters] = useState<any[]>([]);
  const [isLoadingApplyFilter, setIsLoadingApplyFilter] = useState(false);

  const { sortBy, sortDirection, workspace } = filterValues;

  const filterSorting = useMemo(
    () => ({ sortBy, sortDirection }),
    [sortBy, sortDirection]
  );

  const setFilterSorting = useCallback(
    (sortingValues: Record<string, any>) => {
      onChange({
        ...filterValues,
        ...sortingValues,
      });
    },
    [filterValues, onChange]
  );

  const displayedStudiesCount = filteredStudies === null ? 0 : numOfStudies;

  const applyFilter = useCallback(
    async (selectedFilters: (string | number)[]) => {
      if (!selectedFilters?.length) {
        setFilteredStudies([]);
        setFilterApplied([]);
        return;
      }

      setIsLoadingApplyFilter(true);
      try {
        const queryFilterId = selectedFilters.join('&filter=');
        const { data } = await HttpClient.get(
          `/dicomweb/studies/apply_filter?workspace=${workspace}&filter=${queryFilterId}`
        );

        if (data?.length) {
          setFilteredStudies(data);
          setFilterApplied(selectedFilters);
        } else {
          toast.error(t('AdvancedFilters:Studies not found'));
          setFilteredStudies(null);
          setFilterApplied(selectedFilters);
        }
      } catch (error) {
        console.error('Error applying filter:', error);
        toast.error(t('Common:Server Error'));
        setFilteredStudies(null);
      } finally {
        setIsLoadingApplyFilter(false);
      }
    },
    [workspace, setFilteredStudies, setFilterApplied, t]
  );

  useEffect(() => {
    let isMounted = true;

    const loadFilters = async () => {
      try {
        const { data } = await HttpClient.get('/custom-filters');
        if (!isMounted) return;

        setCustomFilters(data || []);
        const favoriteFilter = data?.find((filter: any) => filter.favorite)?.id;
        const favoriteFilterArray = favoriteFilter ? [favoriteFilter] : [];
        const currentFilter = filterApplied.length ? filterApplied : favoriteFilterArray;

        if (currentFilter.length) {
          await applyFilter(currentFilter);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error('Error loading filters:', error);
        toast.error(t('AdvancedFilters:Failed to load filters'));
      }
    };

    loadFilters();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClearFilters = useCallback(() => {
    clearFilters();
    setFilterApplied([]);
  }, [clearFilters, setFilterApplied]);

  const handleOpenAdvancedFilters = useCallback(() => {
    show({
      content: AdvancedFilters,
      title: t('StudyList:Advanced Filters'),
      overflow: false,
      fullsize: true,
      shouldCloseOnEsc: true,
      contentProps: {
        applyFilter,
        customFilters,
        setCustomFilters,
        fullsize: true,
      },
      containerDimensions: 'max-w-4xl max-h-4xl',
    });
  }, [show, t, applyFilter, customFilters]);

  const filterOptions = useMemo(() => {
    const options: any[] = customFilters.map(filter => ({
      icon: filter.favorite ? 'filter-filled' : 'filter',
      title: filter.name,
      onClick: () => applyFilter([filter.id]),
    }));

    options.push({
      icon: 'icon-add',
      title: t('StudyList:New filter'),
      onClick: handleOpenAdvancedFilters,
    });

    return options;
  }, [customFilters, t, applyFilter, handleOpenAdvancedFilters]);

  const filterButtonText = useMemo(() => {
    if (filterApplied.length === 0) {
      return t('StudyList:Advanced Filters');
    }

    if (filterApplied.length === 1) {
      return (
        customFilters.find(filter => filter.id === filterApplied[0])?.name ||
        t('StudyList:Advanced Filters')
      );
    }

    return t('AdvancedFilters:Multiple filters');
  }, [filterApplied, customFilters, t]);

  return (
    <div className="sticky top-0 z-10 study-list-filter-container">
      <div className="study-list-filter-header">
        <div className="relative flex flex-row items-center justify-between p-3">
          <div className="flex flex-row items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg study-list-filter-count-badge">
              <Icon name="count-file" />
              <span className="text-sm font-medium study-list-filter-count-label">
                {t('StudyList:Number of studies')}
              </span>
              <span
                className="text-sm font-bold px-2 py-0.5 rounded-md study-list-filter-count-number"
                data-cy={'num-studies'}
              >
                {displayedStudiesCount}
              </span>
            </div>

            {typeof getDataSourceConfigurationComponent === 'function' &&
              getDataSourceConfigurationComponent()}

            {workspace && (
              <Tooltip position="right" content={t('StudyList:Go to dashboard')}>
                <Link to={`/workspace/${workspace}`} aria-label={t('StudyList:Go to dashboard')}>
                  <button
                    className="flex text-white items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 study-list-filter-icon-btn"
                    aria-label={t('StudyList:Go to dashboard')}
                  >
                    <Icon className="w-4 h-4 text-indigo-400" name="logo-ohif-small" />
                  </button>
                </Link>
              </Tooltip>
            )}
          </div>

          <div className="flex flex-row items-center gap-2">
            {workspace && (
              <Countdown
                isLoadingData={isLoadingData}
                refresh={refresh}
                setIsLoading={setIsLoading}
              />
            )}

            {onUploadClick && (
              <button
                onClick={onUploadClick}
                className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold text-white transition-all duration-200 study-list-filter-process-btn"
              >
                <Icon className="w-4 h-4" name="upload-cloud" />
                {t('StudyList:Process exam')}
              </button>
            )}

            {workspace && (
              <div
                className={classNames(
                  'flex items-center justify-center h-8 rounded-lg transition-all duration-200 study-list-filter-icon-btn',
                  filterApplied.length > 0
                    ? 'bg-indigo-700 border border-indigo-500 text-white'
                    : 'bg-transparent border border-white/20 hover:border-indigo-400/40 text-indigo-400'
                )}
              >
                <Dropdown
                  id="filter-dropdown"
                  showDropdownIcon={true}
                  list={filterOptions}
                  alignment="right"
                  itemsClassName="text-indigo-300"
                >
                  <div className="flex items-center gap-2 pl-2 font-bold">
                    {isLoadingApplyFilter ? <SvgLoading /> : <Icon className="w-4 h-4" name="filter" />}
                    <span className="text-sm">{filterButtonText}</span>
                  </div>
                </Dropdown>
              </div>
            )}

            {isFiltering && (
              <Tooltip position="left" content={t('StudyList:ClearFilters')}>
                <button
                  className="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200 study-list-filter-icon-btn"
                  onClick={handleClearFilters}
                  aria-label={t('StudyList:ClearFilters')}
                >
                  <Icon className="w-4 h-4 text-indigo-400" name="cancel" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <div className="study-list-filter-inputs-wrapper">
        <div className="px-2 pt-3 pb-3 study-list-filter-inputs-content">
          <InputGroup
            inputMeta={filtersMeta}
            values={filterValues}
            onValuesChange={onChange}
            sorting={filterSorting}
            onSortingChange={setFilterSorting}
            isSortingEnabled={IS_SORTING_ENABLED}
          />
        </div>
      </div>

      <div className="study-list-filter-divider" />
    </div>
  );
};

StudyListFilter.propTypes = {
  filtersMeta: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string.isRequired,
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      gridCol: PropTypes.number.isRequired,
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  filterValues: PropTypes.object.isRequired,
  numOfStudies: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  isFiltering: PropTypes.bool.isRequired,
  onUploadClick: PropTypes.func,
  getDataSourceConfigurationComponent: PropTypes.func,
  refresh: PropTypes.func,
  isLoadingData: PropTypes.bool,
  setIsLoading: PropTypes.func,
};

const MemoizedStudyListFilter = memo(StudyListFilter);

export default MemoizedStudyListFilter;
export { MemoizedStudyListFilter as StudyListFilter };
